/**
 * Форма загрузки банковской выписки (карточка BL-2026-08-15-2024, §7.1 п. 1).
 *
 * Flow: файл перетаскивается в зону или выбирается кнопкой → клиентская
 *       проверка «PDF и ≤ 5 МиБ» → «Загрузить» → POST multipart → итог
 *       словами: статус разбора со счётчиками, дубликат со ссылкой на
 *       исходную загрузку или отказ с причиной и кандидатами формата.
 *
 * Зависимости:
 *   - react: состояние формы;
 *   - api.js: uploadBankStatement (перечень разборщиков приходит пропсом —
 *     его читает раздел один раз для формы и фильтра журнала);
 *   - ./BankStatementCommon: бейдж статуса, форматтеры.
 *
 * Связанные ответы 503 (bank_upload.go): сервер различает «загрузка не
 * настроена», «pdftotext недоступен» и «очередь извлечения занята»
 * (pdftext.ErrBusy) только текстом `detail` — см. classifyUnavailable.
 *
 * Связанные файлы:
 *   - src/BankStatementsSection.jsx — размещает форму и обновляет список
 *     после загрузки;
 *   - docs/specs/BL-2026-08-15-2024-bank-statements.md — §6.2 ответы.
 *
 * # КТО МОЖЕТ ЗАГРУЖАТЬ
 *
 * Форма видна всем, кому выдан раздел, но кнопка активна только у admin и
 * backoffice — у остальных рядом пояснение. Решение принимает сервер
 * (`CanWrite`, 403), здесь — только честная подсказка вместо кнопки,
 * которая заведомо получит отказ.
 */

import { useRef, useState } from "react";

import { uploadBankStatement } from "./api";
import {
  MAX_STATEMENT_BYTES,
  StatusBadge,
  WORD_FORMS,
  countWord,
  formatBytes,
  formatMoment,
  formatPeriod,
} from "./BankStatementCommon";

/** Роли, которым сервер разрешает загрузку (domain.CanWrite). */
const UPLOAD_ROLES = ["admin", "backoffice"];

/**
 * Проверка файла до отправки.
 *
 * @param {File|null} file — выбранный файл
 * @returns {string} текст отказа; пустая строка — файл можно отправлять
 *
 * Проверка ТОЛЬКО для удобства: сервер проверяет то же самое (413, 415), и
 * обойти её — значит получить тот же отказ на секунду позже. PDF узнаётся
 * по типу ИЛИ расширению: браузеры на части систем отдают пустой type.
 */
export function validateStatementFile(file) {
  if (!file) return "Файл не выбран.";
  const isPdf =
    file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
  if (!isPdf) return "Ожидается PDF-выписка.";
  if (file.size === 0) return "Файл пустой.";
  if (file.size > MAX_STATEMENT_BYTES)
    return `Файл больше 5 МиБ (${formatBytes(file.size)}) — выписка такого размера не ожидается.`;
  return "";
}

/**
 * Пояснения к видам ответа 503 — что случилось и что делать человеку.
 *
 * Ключ — вид из classifyUnavailable. «busy» нарочно отличается от
 * «unavailable»: занятая очередь проходит сама (повторить через минуту),
 * а недоступный pdftotext — поломка, которую повтор не лечит.
 */
export const UNAVAILABLE_HINTS = {
  busy: "Сервер занят разбором других выписок — повторите загрузку через минуту. Файл не отклонён и в журнал не записан.",
  unavailable: "Извлечение текста из PDF на сервере сейчас не работает — повтор не поможет, сообщите администратору.",
  not_configured: "Загрузка выписок не включена на этом сервере — сообщите администратору.",
  other: "Сервис временно недоступен — повторите позже.",
};

/**
 * Вид отказа 503 загрузки выписки.
 *
 * @param {number} status — код ответа
 * @param {object} data — тело ответа ({detail, reason?})
 * @returns {""|"busy"|"unavailable"|"not_configured"|"other"} пустая
 *          строка — это не 503
 *
 * Сервер (bank_upload.go) отвечает 503 в трёх случаях и различает их
 * машинным полем `reason` (техспека §6.2); текст `detail` — запасной путь
 * для ответа без `reason`:
 *   * `extract_busy` — «извлечение текста занято другими загрузками —
 *     повторите позже» (pdftext.ErrBusy) → busy;
 *   * `extract_unavailable` — «извлечение текста из PDF недоступно»
 *     (pdftext.ErrUnavailable) → unavailable;
 *   * `not_configured` — «загрузка выписок не настроена в этом экземпляре
 *     сервиса» → not_configured.
 * `reason` имеет приоритет над текстом.
 * Неузнанный текст — «other»: тогда показывается сам detail без догадок.
 */
export function classifyUnavailable(status, data) {
  if (status !== 503) return "";
  const reason = (data && data.reason) || "";
  if (reason === "extract_busy" || reason === "busy") return "busy";
  if (reason === "extract_unavailable" || reason === "unavailable") return "unavailable";
  if (reason === "not_configured") return "not_configured";
  const detail = String((data && data.detail) || "").toLowerCase();
  if (detail.includes("занят")) return "busy";
  if (detail.includes("не настроен")) return "not_configured";
  if (detail.includes("недоступн")) return "unavailable";
  return "other";
}

/**
 * Итог загрузки словами.
 *
 * @param {{status: number, data: object}} result — ответ uploadBankStatement
 * @param {function(number): void} onOpen — открыть карточку загрузки
 */
export function UploadResult({ result, onOpen }) {
  const { status, data } = result;

  // 200 — тот же файл уже принят той же версией разборщика (§3.9):
  // разбора не было, и главное — куда смотреть вместо него.
  if (status === 200 && data.upload && data.upload.status === "duplicate") {
    const orig = data.duplicate_of || {};
    const origId = orig.id || data.upload.duplicate_of_id;
    return (
      <div className="notice bs-result">
        <StatusBadge status="duplicate" title={data.upload.status_title} /> Этот
        файл уже загружен {formatMoment(orig.uploaded_at)} пользователем{" "}
        {orig.uploaded_by || "—"} — загрузка №{origId}.{" "}
        {origId && (
          <button type="button" className="link" onClick={() => onOpen(origId)}>
            Открыть исходную
          </button>
        )}
      </div>
    );
  }

  if (status === 201 && data.upload) {
    const u = data.upload;
    return (
      <div className="notice bs-result">
        <StatusBadge status={u.status} title={u.status_title} /> {u.bank_title || u.bank}
        , {formatPeriod(u.period_from, u.period_to)}:{" "}
        {countWord(u.operations, WORD_FORMS.operation)} (новых{" "}
        {u.operations_new}, уже были {u.operations_known}
        {u.operations_conflict > 0 ? `, конфликтов ${u.operations_conflict}` : ""}),{" "}
        {countWord(u.unrecognized, WORD_FORMS.unrecognizedLine)}.{" "}
        <button type="button" className="link" onClick={() => onOpen(u.id)}>
          Открыть
        </button>
        {u.status === "invariant_failed" && (
          <p className="error-text">
            Итоги банка не сошлись с разбором — в учёт не принято ничего.
            Перечень несошедшихся проверок — в карточке.
          </p>
        )}
      </div>
    );
  }

  // Отказ. У 422 загрузка записана со статусом failed (§6.2), и кандидаты
  // формата объясняют, почему банк не узнан, — отсюда подсказка выбрать
  // банк явно.
  const candidates = Array.isArray(data.candidates) ? data.candidates : [];
  // 503: что делать — словами, а ответ сервера — следом мелко, чтобы
  // администратор узнал его в журнале.
  const unavailable = classifyUnavailable(status, data);
  if (unavailable) {
    return (
      <div
        className={`${unavailable === "busy" ? "notice warn" : "error"} bs-result`}
        role="alert"
        data-unavailable={unavailable}
      >
        <div>{UNAVAILABLE_HINTS[unavailable]}</div>
        {data.detail && <div className="cell-note muted">Ответ сервера: {data.detail}</div>}
      </div>
    );
  }
  return (
    <div className="error bs-result" role="alert">
      <div>{data.detail || `Ошибка ${status}`}</div>
      {status === 422 && data.upload_id && (
        <div>
          Загрузка записана как неудачная — №{data.upload_id}.{" "}
          <button
            type="button"
            className="link"
            onClick={() => onOpen(data.upload_id)}
          >
            Открыть
          </button>
        </div>
      )}
      {candidates.length > 0 && (
        <div className="bs-candidates">
          Похожие форматы (выберите банк явно, если знаете его):
          <ul>
            {candidates.map((c) => (
              <li key={c.bank}>
                {c.bank} — совпадение {c.score}
                {c.evidence && c.evidence.length > 0 && (
                  <span className="muted"> · признаки: {c.evidence.join("; ")}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Форма загрузки выписки.
 *
 * @param {object} user — текущий пользователь; роль решает, активна ли кнопка
 * @param {Array<{code: string, title: string, version: string}>} parsers —
 *        разборщики (§6.1); пустой перечень оставляет автоопределение
 * @param {function(object): void} onUploaded — после ответа сервера (любого,
 *        кроме сетевого отказа): список загрузок обновляется сразу
 * @param {function(number): void} onOpen — открыть карточку загрузки
 */
export default function BankStatementUploadPanel({ user, parsers = [], onUploaded, onOpen }) {
  const [bank, setBank] = useState("");
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [netError, setNetError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const canUpload = UPLOAD_ROLES.includes(user?.role);

  function pick(files) {
    setResult(null);
    setNetError("");
    if (!files || files.length === 0) return;
    if (files.length > 1) {
      setFile(null);
      setFileError("Перетащите один файл — выписки загружаются по одной.");
      return;
    }
    const f = files[0];
    setFile(f);
    setFileError(validateStatementFile(f));
  }

  async function submit() {
    if (busy || !file || fileError) return;
    setBusy(true);
    setResult(null);
    setNetError("");
    try {
      const res = await uploadBankStatement(file, bank);
      setResult(res);
      if (res.ok) {
        // Файл отправлен — форма готова к следующему; при отказе файл
        // остаётся, чтобы повторить с явно выбранным банком.
        setFile(null);
        if (inputRef.current) inputRef.current.value = "";
      } else if (classifyUnavailable(res.status, res.data) === "busy") {
        // Не отказ файла, а занятая очередь сервера — повтор пройдёт.
        console.warn("загрузка выписки отложена: сервер занят", res.status, res.data.detail);
      } else {
        console.error("загрузка выписки отклонена", res.status, res.data.detail);
      }
      onUploaded && onUploaded(res);
    } catch (e) {
      setNetError(`Сервер не ответил: ${e.message}`);
      console.error("загрузка выписки не удалась", e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h2>Загрузка выписки</h2>

      {!canUpload && (
        <p className="muted">
          Загрузка доступна бэкофису и администратору. Журнал загрузок и
          карточки выписок открыты для просмотра.
        </p>
      )}

      <div
        className={`bs-dropzone ${dragOver ? "bs-dropzone-over" : ""} ${canUpload ? "" : "bs-dropzone-off"}`}
        data-testid="bs-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          if (canUpload) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (canUpload && !busy) pick(e.dataTransfer.files);
        }}
      >
        <p>Перетащите PDF-выписку сюда или</p>
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,application/pdf"
          aria-label="файл выписки"
          disabled={!canUpload || busy}
          onChange={(e) => pick(e.target.files)}
        />
        {file && (
          <p className="muted">
            {file.name} · {formatBytes(file.size)}
          </p>
        )}
      </div>
      {fileError && <p className="error-text">{fileError}</p>}

      <div className="filters bs-upload-controls">
        <select
          value={bank}
          onChange={(e) => setBank(e.target.value)}
          aria-label="банк"
          disabled={!canUpload || busy}
        >
          <option value="">Определить автоматически</option>
          {parsers.map((p) => (
            <option key={p.code} value={p.code}>
              {p.title} ({p.version})
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={submit}
          disabled={!canUpload || busy || !file || Boolean(fileError)}
        >
          {busy ? "Разбираем…" : "Загрузить"}
        </button>
      </div>

      {netError && <p className="error">{netError}</p>}
      {result && <UploadResult result={result} onOpen={onOpen} />}
    </section>
  );
}
